import moment from "moment-timezone";
import { ParkingRequestTypeIdConstant, HMI_ENTRY } from "../constants/DatabaseConstants/ParkingConstants";
import { ParkingHistoryReportData, InTimeOutTimeReportData } from "../shared/interface/Report/allUsersReport.interface";

export class ParkingReportUtil {
  private static createIntTimeOutTimeObj = (requestTypeId: ParkingRequestTypeIdConstant, parkingRequest: ParkingHistoryReportData) => {
    const firstName = parkingRequest.keyFobAllocation?.mobileUser?.first_name;
    const lastName = parkingRequest.keyFobAllocation?.mobileUser?.last_name;
    const userFullName = firstName ? firstName + " " + lastName : HMI_ENTRY;

    const obj: InTimeOutTimeReportData = {
      id: parkingRequest.id,
      key_fob_id: parkingRequest.key_fob_id,
      key_fob_token: parkingRequest.keyFob.key_fob_token,
      key_fob_allocation_master_id: parkingRequest.keyFobAllocation?.id,
      is_visitor: parkingRequest.keyFobAllocation?.is_visitor,
      vehicle_number: parkingRequest.keyFobAllocation?.vehicle_number as unknown as string | undefined,
      vehicle_make: parkingRequest.keyFobAllocation?.vehicle_make as unknown as string | undefined,
      full_name: userFullName,
      mobile_number: parkingRequest.keyFobAllocation?.mobileUser?.mobile_number
    };

    switch (requestTypeId) {
      case ParkingRequestTypeIdConstant.STORAGE: {
        obj["in_time"] = parkingRequest.created_on;
        obj["parkedBy"] = userFullName;
        break;
      }

      case ParkingRequestTypeIdConstant.RETRIEVAL: {
        obj["out_time"] = parkingRequest.created_on;
        obj["retrievedBy"] = userFullName;
        break;
      }

      default:
        throw new Error(`Parking request type id not found when creating in-time and out-time request data request_type_id=${requestTypeId}`);
    }

    return obj;
  };

  private static calculateTimeDifference = (inTime: string | null, outTime: string | null): string | null => {
    if (!inTime || !outTime) return null;

    const inMoment = moment(inTime, "DD-MM-YYYY HH:mm:ss");
    const outMoment = moment(outTime, "DD-MM-YYYY HH:mm:ss");

    if (!inMoment.isValid() || !outMoment.isValid()) return null;
    if (outMoment.isBefore(inMoment)) return null;

    const hours = outMoment.diff(inMoment, "hours");
    const minutes = outMoment.diff(inMoment, "minutes") % 60;

    const hoursText = hours > 0 ? `${hours} hour${hours !== 1 ? "s" : ""}` : "";
    const minutesText = minutes > 0 ? `${minutes} minute${minutes !== 1 ? "s" : ""}` : "";

    return [hoursText, minutesText].filter(Boolean).join(" ").trim() || null;
  };

  public static formatParkingHistoryList = (parkingList: ParkingHistoryReportData[]): InTimeOutTimeReportData[] => {
    const formattedParkingHistoryList: InTimeOutTimeReportData[] = [];
    const parkingListDeepCopy: Array<ParkingHistoryReportData | null> = JSON.parse(JSON.stringify(parkingList));

    for (let i = 0; i < parkingListDeepCopy.length; i++) {
      const currentRequest = parkingListDeepCopy[i];

      if (!currentRequest) continue;

      /* storage request */
      if (currentRequest.request_type_id === ParkingRequestTypeIdConstant.STORAGE) {
        const storageRequest = ParkingReportUtil.createIntTimeOutTimeObj(ParkingRequestTypeIdConstant.STORAGE, currentRequest);

        /* find a matching retrieval request. */
        let retrievalRequest: InTimeOutTimeReportData | null = null;
        for (let j = i + 1; j < parkingListDeepCopy.length; j++) {
          const nextParkingRequest = parkingListDeepCopy[j];

          if (!nextParkingRequest) continue;

          /* check if there is a next request with same key-fob id */
          if (nextParkingRequest.key_fob_id !== currentRequest.key_fob_id) continue;

          /* check if next request is booking */
          if (nextParkingRequest.request_type_id === ParkingRequestTypeIdConstant.STORAGE) break;
          /* check if next request is retrieval */  // prettier-ignore
          else if (nextParkingRequest.request_type_id === ParkingRequestTypeIdConstant.RETRIEVAL) {
            retrievalRequest = ParkingReportUtil.createIntTimeOutTimeObj(ParkingRequestTypeIdConstant.RETRIEVAL, nextParkingRequest);
            const booking_retrieval_time_diff = ParkingReportUtil.calculateTimeDifference(currentRequest?.created_on, nextParkingRequest?.created_on);
            retrievalRequest['booking_retrieval_time_diff'] = booking_retrieval_time_diff;
            
            /* set the value to null, we do not want to consider this value back for any other booking request or treat as a single retrieval request */
            parkingListDeepCopy[j] = null
            break;

          }
        }
        retrievalRequest ? formattedParkingHistoryList.unshift({ ...retrievalRequest, ...storageRequest }) : formattedParkingHistoryList.unshift(storageRequest);
      } else if (currentRequest.request_type_id === ParkingRequestTypeIdConstant.RETRIEVAL) {
        /* when a single retrieval request is found without a matching booking request pair.*/
        const retrievalRequest = ParkingReportUtil.createIntTimeOutTimeObj(ParkingRequestTypeIdConstant.RETRIEVAL, currentRequest);

        formattedParkingHistoryList.unshift(retrievalRequest);
      }
    }

    return formattedParkingHistoryList;
  };
}
